/**
 * Holds the per-tab portal id (`tabPortalId`) in memory and the cross-tab
 * portal id (`sessionPortalId`) in `localStorage`. Used by HttpService to
 * attach `X-Current-Portal-Id` and `X-Session-Portal-Id` on outgoing requests.
 *
 * The actual mismatch validation lives on the server (Laravel + rms-api).
 */
const STORAGE_KEY = 'sessionPortalId';
const WINDOW_NAME_TOKEN_PREFIX = 'portal-';

class PortalContextService {
    private _tabPortalId: number | undefined;

    constructor() {
        // The parent (shopify-app) seeds the iframe's `name` attribute with a
        // `portal-<shop-id>` token on every page render. `window.name` is
        // populated synchronously by the browser before any script in the
        // iframe executes, so this is the earliest possible seed and avoids
        // any dependency on a runtime call or URL exposure.
        const fromWindowName = this.readSessionPortalIdFromWindowName();
        if (fromWindowName !== undefined) {
            localStorage.setItem(STORAGE_KEY, String(fromWindowName));
            this._tabPortalId = fromWindowName;
        }
    }

    private readSessionPortalIdFromWindowName(): number | undefined {
        try {
            // Parent seeds a space-separated name (e.g. "rms-client-iframe portal-42"); split into tokens to find ours.
            const tokens = (window.name || '').split(/\s+/);
            const token = tokens.find(t => t.startsWith(WINDOW_NAME_TOKEN_PREFIX));
            if (!token) {
                return undefined;
            }
            const parsed = Number(token.slice(WINDOW_NAME_TOKEN_PREFIX.length));
            return parsed > 0 ? parsed : undefined;
        } catch {
            return undefined;
        }
    }

    public get tabPortalId(): number | undefined {
        return this._tabPortalId;
    }

    public setTabPortalId(id: number): void {
        this._tabPortalId = id;
    }

    public getSessionPortalId(): number | undefined {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw === null ? undefined : Number(raw);
    }

    public clearSessionPortalId(): void {
        localStorage.removeItem(STORAGE_KEY);
    }
}

export default new PortalContextService();
